import React, { useState } from 'react';
import { capitalize, isEmpty } from 'lodash';

import ClaimLink from '~/components/ClaimLink';
import CliveRecommendationPanel from '~/components/Clive/CliveRecommendationPanel/CliveRecommendationPanel';
import CliveRecommendationPanelButton from '~/components/Clive/CliveRecommendationPanel/CliveRecommendationPanelButton';
import RecommendedPolicyDisplay from '~/components/Clive/CliveRecommendationPanel/RecommendedPolicyDisplay';
import CliveSummaryCard from '~/components/Clive/SummaryCard/CliveSummaryCard';
import type { ClassificationReason, Recommendation } from '~/components/Clive/types';
import mixpanel from '~/components/CmsMain/mixpanel';
import { CommunicationAttachToKnownClaimButton } from '~/components/communications/CommunicationAttachToKnownClaimButton';
import type {
  BundleClassification,
  GeneralEmailAnalysisExtra,
  RelatedPolicyRecommendation,
} from '~/components/communications/GeneralEmailCommunicationAiAnalysis/generalEmailAnalysisExtra';
import { CommunicationAiAnalysisVersion } from '~/components/communications/GeneralEmailCommunicationAiAnalysis/generalEmailAnalysisExtra';
import {
  doesFnolCategoryExists,
  getFnolCategoryClassification,
} from '~/components/communications/GeneralEmailCommunicationAiAnalysis/GeneralEmailCommunicationAiAnalysisUtils';
import Caption from '~/components/core/TextComponents/Caption';
import Label from '~/components/core/TextComponents/Label';
import Text from '~/components/core/TextComponents/Text';
import { PolicyCard } from '~/components/Fnol/FnolAiInbox/PolicySelectionCard/PolicyCard';
import type { Policy } from '~/components/Fnol/FnolAiInbox/types';
import { useGQClassification } from '~/components/hooks/useGQClassification';
import Divider from '~/components/ui/atoms/Divider';
import { MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import type { FnolInboxRecord } from '~/server_shared/openapi/data-contracts';

interface GeneralEmailCommunicationAiAnalysisActions {
  onAttachToClaim?: (claimId: number, extraParams: Record<string, unknown>) => void;
  onMoveToFnolQueue?: () => void;
}

interface GeneralEmailCommunicationAiAnalysisBannerProps {
  communicationType: string;
  communicationId: string | number;
  communicationAiAnalysis: GeneralEmailAnalysisExtra;
  communicationAiAnalysisActions: GeneralEmailCommunicationAiAnalysisActions;
  record?: FnolInboxRecord;
}

interface ClassificationReasonsProps {
  reasons: ClassificationReason[];
}

export const ClassificationReasons: React.FC<ClassificationReasonsProps> = ({ reasons }) => {
  if (isEmpty(reasons)) return null;

  return (
    <div className="mb-12">
      <ul className="list-none p-0">
        {reasons.map((reason, index) => (
          <li key={index} className="mb-4">
            <Text variant={Text.VARIANTS.SM}>{reason.reason}</Text>
            {reason.quotes && !isEmpty(reason.quotes) ? (
              reason.quotes.map((quote, quoteIndex) => (
                <React.Fragment key={`${quoteIndex}-${quote.location_desc}`}>
                  <Label>{quote.location_desc}</Label>
                  <div className="border-gray-300 ml-8 border-l-2 pl-8">
                    <Caption className="text-gray-600 italic">&quot;{quote.quote}&quot;</Caption>
                  </div>
                </React.Fragment>
              ))
            ) : (
              <Caption className="text-gray-500 italic">No source quotes available</Caption>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

const retrievePolicyRecommendation = (analysis: GeneralEmailAnalysisExtra): RelatedPolicyRecommendation | null => {
  if (!analysis.classification_response) {
    return null;
  }
  const { related_policy_recommendation } = analysis.classification_response;
  return related_policy_recommendation || null;
};

const validateAnalysis = (
  analysis: GeneralEmailAnalysisExtra | undefined | null
): GeneralEmailAnalysisExtra | false => {
  if (!analysis || isEmpty(analysis)) {
    return false;
  }

  if (analysis.version !== CommunicationAiAnalysisVersion) {
    return false;
  }

  if (!analysis.classification_response || isEmpty(analysis.classification_response)) {
    return false;
  }

  return analysis;
};

const retrieveRecommendation = (
  analysis: GeneralEmailAnalysisExtra | undefined | null,
  communicationType: string,
  communicationId: string | number,
  communicationAiAnalysisActions: GeneralEmailCommunicationAiAnalysisActions,
  shouldDisplayAttachToClaim: boolean,
  shouldDisplayMoveToFnol: boolean,
  shouldDisplayFnolClassification: boolean
): Recommendation | null => {
  const validatedAnalysis = validateAnalysis(analysis);
  if (!validatedAnalysis) {
    return null;
  }

  const { related_claim_recommendation } = validatedAnalysis.classification_response!;

  if (related_claim_recommendation?.claim_number) {
    const onAttachToClaim = communicationAiAnalysisActions.onAttachToClaim;
    return {
      title: `This ${communicationType} is a claim update - ${related_claim_recommendation.claim_number}`,
      classification_reasons:
        related_claim_recommendation.classification_reasons || related_claim_recommendation.structured_explanation,
      explanation_component: (
        <ClaimLink
          claimId={related_claim_recommendation.claim_internal_information['claim_id'] as number}
          linkText={related_claim_recommendation.claim_number}
        />
      ),
      actionComponent:
        onAttachToClaim && shouldDisplayAttachToClaim ? (
          <CommunicationAttachToKnownClaimButton
            claimId={related_claim_recommendation.claim_internal_information.claim_id as number}
            communicationId={Number(communicationId)}
            communicationChannel="email"
            onAttach={async () => {
              // The onAttachOverride handles the logic, so this can be a no-op
            }}
            onAttachOverride={async (claimId: number, attachExtraParams: Record<string, unknown>) => {
              mixpanel.track(MIXPANEL_EVENTS.GQ_AI_ATTACH_TO_CLAIM_CLICKED, {
                communication_id: communicationId,
              });
              await onAttachToClaim(claimId, attachExtraParams);
            }}
            buttonComponent={({ onClick, disabled }) => (
              <CliveRecommendationPanelButton text="Attach to Claim" onClick={onClick} disabled={disabled} />
            )}
          />
        ) : null,
    };
  }

  if (doesFnolCategoryExists(validatedAnalysis) && shouldDisplayFnolClassification) {
    const fnol_classification = getFnolCategoryClassification(validatedAnalysis) as BundleClassification;
    return {
      title: `This ${communicationType} is a new claim`,
      explanation_component: <></>,
      classification_reasons: fnol_classification.classification_reasons,
      actionComponent: shouldDisplayMoveToFnol ? (
        <CliveRecommendationPanelButton
          text="Move to FNOL Inbox"
          onClick={() => communicationAiAnalysisActions.onMoveToFnolQueue?.()}
        />
      ) : null,
    };
  }

  return null;
};

export const GeneralEmailCommunicationAiAnalysisBanner: React.FC<GeneralEmailCommunicationAiAnalysisBannerProps> = ({
  communicationId,
  communicationAiAnalysis,
  communicationType,
  communicationAiAnalysisActions,
}) => {
  const [showPolicy, setShowPolicy] = useState(false);
  const { isAllowedToPerformAction, isMoveToFnolEnabled, isFnolCategoryClassificationEnabled } = useGQClassification();

  if (!communicationAiAnalysis || communicationAiAnalysis.version !== CommunicationAiAnalysisVersion) {
    return null;
  }

  const recommendation = retrieveRecommendation(
    communicationAiAnalysis,
    communicationType,
    communicationId,
    communicationAiAnalysisActions,
    isAllowedToPerformAction,
    isMoveToFnolEnabled,
    isFnolCategoryClassificationEnabled
  );

  const recommendedPolicy = retrievePolicyRecommendation(communicationAiAnalysis);
  const summary = communicationAiAnalysis.classification_response?.summary;

  const bannerTitle = recommendation ? "Clive's Assessment" : `${capitalize(communicationType)} Summary`;

  if (!recommendation && !summary && !recommendedPolicy) return null;

  return (
    <>
      <CliveRecommendationPanel
        title={bannerTitle}
        subtitle={recommendation ? recommendation.title : undefined}
        actionComponent={recommendation ? recommendation.actionComponent : null}
        openByDefault={false}
      >
        <div className="relative flex w-full flex-col gap-12">
          {recommendedPolicy && recommendedPolicy.policy_number ? (
            <RecommendedPolicyDisplay
              policyNumber={recommendedPolicy.policy_number}
              onPolicySelection={() => setShowPolicy(true)}
              viewOnly
            />
          ) : null}

          {recommendation && (
            <>
              {recommendedPolicy && <Divider />}
              {recommendation.explanation_component}
              {recommendation.classification_reasons && (
                <ClassificationReasons reasons={recommendation.classification_reasons} />
              )}
            </>
          )}

          {!isEmpty(summary) && summary && (
            <CliveSummaryCard title={`${capitalize(communicationType)} Summary`} summaryText={summary} />
          )}
        </div>
      </CliveRecommendationPanel>
      {showPolicy && recommendedPolicy?.policy_data ? (
        <PolicyCard
          policy={recommendedPolicy?.policy_data as Policy}
          onChangePolicy={() => null}
          onCancel={() => setShowPolicy(false)}
          viewOnly
        />
      ) : null}
    </>
  );
};

export default GeneralEmailCommunicationAiAnalysisBanner;
